// Marks that our own code has just started a full page load away (the login
// code page sending the host to the dashboard). WebKit then cancels requests
// still in flight, such as the rest of a server action's response, and
// reports a stackless "TypeError: Load failed" that nothing can act on.
// PostHog's before_send drops only that error, only while the page that set
// the mark is still showing (posthog-before-send.ts).

// A real navigation unloads the page well within this; if the page is still
// here after it, the navigation did not happen and errors count again.
const LEAVING_PAGE_WINDOW_MS = 10_000;
// Guest upload pages never drop an error, whatever the mark says.
const UPLOAD_PATH = /^\/(?:[^/]+\/)?upload(?:\/|$)/;

interface LeavingPageMark {
  at: number;
  path: string;
  /** Removes the mark and its listeners. */
  end: () => void;
}

interface LeavingPageWindow {
  __guestGalleryLeavingPage?: unknown;
}

export function isUploadPath(pathname: string): boolean {
  return UPLOAD_PATH.test(pathname);
}

function readMark(): Pick<LeavingPageMark, "at" | "path"> | null {
  if (typeof window === "undefined" || !window) return null;
  const mark = (window as unknown as LeavingPageWindow).__guestGalleryLeavingPage;
  if (!mark || typeof mark !== "object") return null;
  const { at, path } = mark as Partial<LeavingPageMark>;
  return typeof at === "number" && typeof path === "string" ? { at, path } : null;
}

/** Call immediately before window.location.assign/replace. */
export function markLeavingPage(): void {
  endLeavingPage();
  const target = window;
  const w = target as unknown as LeavingPageWindow;
  const end = (): void => {
    if (w.__guestGalleryLeavingPage === mark) delete w.__guestGalleryLeavingPage;
    target.removeEventListener("popstate", end);
    target.removeEventListener("pageshow", end);
  };
  const mark: LeavingPageMark = { at: Date.now(), path: target.location.pathname, end };
  w.__guestGalleryLeavingPage = mark;
  // Back/forward within the app, or back from the next page (back/forward
  // cache): the visitor is on a page again.
  target.addEventListener("popstate", end);
  target.addEventListener("pageshow", end);
}

/**
 * Ends the mark for good and removes its listeners: a new mark, the code
 * form unmounting (a client-side navigation away), and every check below.
 */
export function endLeavingPage(): void {
  try {
    if (typeof window === "undefined" || !window) return;
    const w = window as unknown as LeavingPageWindow;
    const mark = w.__guestGalleryLeavingPage;
    if (mark && typeof mark === "object" && typeof (mark as Partial<LeavingPageMark>).end === "function") {
      (mark as LeavingPageMark).end();
    }
    delete w.__guestGalleryLeavingPage;
  } catch {
    // Never let the mark break analytics or the page.
  }
}

/**
 * Ends the mark once the page shown is not the marked one (the window's path,
 * or the path of an event about to be sent), or once it ran out. before_send
 * calls this for every event, so a round trip away and back (pushState,
 * replaceState) cannot bring the mark back.
 */
export function endLeavingPageIfMoved(eventPathname?: string | null, now: number = Date.now()): void {
  try {
    const mark = readMark();
    if (!mark) return;
    const pathname = window.location?.pathname;
    const hasMoved = pathname !== mark.path || (typeof eventPathname === "string" && eventPathname !== mark.path);
    const hasExpired = now < mark.at || now - mark.at >= LEAVING_PAGE_WINDOW_MS;
    if (hasMoved || hasExpired) endLeavingPage();
  } catch {
    endLeavingPage();
  }
}

/** True shortly after markLeavingPage while the marked page is still showing. */
export function isLeavingPage(now: number = Date.now()): boolean {
  endLeavingPageIfMoved(undefined, now);
  const mark = readMark();
  return mark !== null && !isUploadPath(mark.path);
}
